import React from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { FaPenNib, FaImage, FaMicrophone, FaMusic, FaVideo } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const CallToAction: React.FC = () => {
  const contentRef = useScrollAnimation();

  const features = [
    {
      icon: <FaPenNib />,
      label: "Poetry"
    },
    {
      icon: <FaImage />,
      label: "Images"
    },
    {
      icon: <FaMicrophone />,
      label: "Recitation"
    },
    {
      icon: <FaMusic />,
      label: "Music"
    },
    {
      icon: <FaVideo />,
      label: "Video"
    }
  ];

  return (
    <section className="relative py-24 md:py-32">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/30 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-purple-900/10 to-black/50" />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div 
          ref={contentRef}
          className="rounded-2xl bg-gradient-to-br from-purple-900/20 to-indigo-900/20 p-8 sm:p-10 md:p-12
                   border border-white/10 backdrop-blur-sm opacity-0 translate-y-8 duration-700 ease-out
                   shadow-xl shadow-purple-500/5 hover:shadow-purple-500/10 transition-all"
        >
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-white">
              Live Your Verse with Parlando
            </h2>
            
            <p className="text-base sm:text-lg text-gray-300 mb-8">
              Transform your words into multi-sensory poetic experiences that captivate the heart and mind.
              Unlock the full potential of your creative expression through the power of AI.
            </p>
            
            {/* Feature icons */}
            <div className="flex justify-center gap-4 sm:gap-6 md:gap-10 mb-10">
              {features.map((feature, index) => (
                <div key={index} className="text-center group">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 
                                flex items-center justify-center text-white mb-2 mx-auto
                                shadow-lg shadow-purple-500/20 group-hover:shadow-purple-500/40
                                group-hover:scale-110 transition-all duration-300">
                    {feature.icon}
                  </div>
                  <span className="text-xs text-gray-300 group-hover:text-white transition-colors duration-300">
                    {feature.label}
                  </span>
                </div>
              ))}
            </div>
            
            {/* Poetic tagline */}
            <p className="text-lg sm:text-xl italic text-gray-100 mb-8 font-serif">
              "Where imagination flows, your verses glow, and poetry comes alive."
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/dashboard">
                <button className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-600 text-white 
                                shadow-lg shadow-purple-500/20 hover:shadow-xl hover:shadow-purple-500/30 
                                hover:scale-[1.02] transition-all duration-300 min-w-[180px]
                                inline-flex items-center justify-center gap-2 group">
                  <span className="group-hover:translate-x-1 transition-transform duration-300 
                               inline-flex items-center gap-2">
                    Start Creating 
                    <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none">
                      <path d="M1 8h14M9 2l6 6-6 6" stroke="currentColor" strokeWidth="2" 
                            strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                </button>
              </Link>
              
              <button 
                className="px-6 py-3 rounded-xl border-2 border-white/20 text-white
                        hover:bg-white/10 transition-all duration-300 min-w-[180px]"
              >
                <span className="relative z-10 transition-transform duration-300">
                  Explore Examples
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CallToAction; 